import { useEffect, useState } from 'react';
import { cn } from '../cn.js';
import { ClockDigits } from './ClockDigits.js';
import { formatDuration, type TimerFormat } from './internal/formatDuration.js';

export interface ElapsedTimerProps {
  /** The instant counting started, e.g. a `TrainingSession.startedAt`. */
  startedAt: Date;
  /** `HH:MM:SS` (default) for a session-length watch; `Dd HH:MM` once elapsed time can plausibly
   *  span days. */
  format?: TimerFormat;
  className?: string;
  /** Overrides the generic "Elapsed time" aria-label — useful when several timers are on one
   *  screen and each needs to be distinguishable to a screen reader. */
  ariaLabel?: string;
}

/**
 * A running duration counted up from `startedAt` — a training session's live watch time, a
 * recording's own elapsed clock. Ticks once a second via its own `setInterval`, cleaned up on
 * unmount. The tick only triggers a re-render; the displayed value is always recomputed from
 * `startedAt` and `Date.now()` rather than accumulated in state, so a throttled/backgrounded tab
 * never drifts the number shown once it resumes — it just jumps straight to the correct value.
 *
 * The digits render through `ClockDigits` (tabular figures, softened separators, the changed group
 * easing in), so this reads like every other live clock in the app. It inherits the surrounding
 * font size, so it sits inline in a banner sentence or a pill as easily as on its own.
 */
export function ElapsedTimer({
  startedAt,
  format = 'HH:MM:SS',
  className,
  ariaLabel = 'Elapsed time',
}: ElapsedTimerProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const seconds = (now - startedAt.getTime()) / 1000;

  return (
    <span role="timer" aria-label={ariaLabel} className={cn('inline-flex', className)}>
      <ClockDigits value={formatDuration(seconds, format)} live />
    </span>
  );
}
